import { IEnhancedOrderItem } from "@faire/web-api/indigofair/data/IEnhancedOrderItem";
import { IProductOption } from "@faire/web-api/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { ProductVariationType } from "@faire/web-api/indigofair/data/ProductVariationType";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import uniq from "lodash/uniq";

import { getProductOptions } from "@faire/retailer/views/product/utils/productUtils";

// The maximum number of characters that a SKU can be for it to be displayed.
// Any longer, and we will not display it.
const SKU_MAX_LENGTH = 20;

/**
 * Checks whether SKU is valid. SKU is valid if it is not undefined
 * and does not exceed the maximum character display length.
 * @param sku
 */
export const isValidSKU = (sku: string | undefined) => {
  return (sku?.length ?? Number.POSITIVE_INFINITY) <= SKU_MAX_LENGTH;
};

/**
 * Returns whether the option's variations (except for Size) all have the same value as the ones from optionToMatch.
 * @param option
 * @param optionToMatch
 */
const openSizingOptionsHaveSameVariationValues = (
  option: IProductOption,
  optionToMatch: IProductOption
) => {
  return option.variations.every((variation) => {
    if (variation.type === ProductVariationType.SIZE) {
      return true;
    } else {
      // All the variations (for example, colour) that are common between option and optionToMatch must have the same values.
      return optionToMatch.variations.every((matchVariation) => {
        if (
          matchVariation.name === variation.name &&
          matchVariation.value !== variation.value
        ) {
          return false;
        }
        return true;
      });
    }
  });
};

/**
 * Returns the SKUs in the given product as a string. SKUs of lengths over the limit
 * will not be in the string. SKUs for product options that have different colors, styles, etc
 * (everything aside from Size) from those of optionToMatch will also not be in the string.
 * Each SKU is separated by a comma. Removes duplicates.
 * @param product
 * @param optionToMatch
 */
export const getOpenSizingSKUsFromProduct = (
  product: IProductPageResponse.IPdpProduct,
  optionToMatch?: IProductOption
) => {
  return uniq(
    getProductOptions(product)
      .filter((option) => {
        return optionToMatch
          ? openSizingOptionsHaveSameVariationValues(option, optionToMatch)
          : null;
      })
      .map((option) =>
        isValidSKU(option.brand_code) ? option.brand_code : undefined
      )
      .filter(isNotUndefined)
  ).join(", ");
};

/**
 * Returns all the SKUs in the given OrderItem as a string. SKUs of lengths over the limit
 * will not be in the string. Each SKU is separated by a comma.
 * @param product
 */
export const getAllSKUsFromOrderItem = (orderItem: IEnhancedOrderItem) => {
  return orderItem.brand_codes
    .map((sku) => (isValidSKU(sku) ? sku : undefined))
    .filter(isNotUndefined)
    .join(", ");
};
